import React, { useEffect, useState } from "react";
import { Trophy, Calendar, Flag } from "lucide-react";
import { getTable, getMatches } from "../lib/api";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";

const LabeledBadge = ({ text, tone = "neutral" }) => {
  const styles = {
    W: "bg-[#6CABDD] text-[var(--brand-ink)]",
    D: "bg-[#E2E8F0] text-[var(--brand-ink)]",
    L: "bg-[#0A0F17] text-white",
    neutral: "bg-[#F1F5F9] text-[var(--brand-body)]",
  };
  return (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded-sm font-display text-xs font-black ${styles[tone] || styles.neutral}`}
    >
      {text}
    </span>
  );
};

const resultTone = (m) => {
  if (m.home_score == null || m.away_score == null) return "neutral";
  const covScore = m.coventry_is_home ? m.home_score : m.away_score;
  const oppScore = m.coventry_is_home ? m.away_score : m.home_score;
  if (covScore > oppScore) return "W";
  if (covScore < oppScore) return "L";
  return "D";
};

export const MatchCenter = () => {
  const copy = usePageContent("stats");
  const [table, setTable] = useState(null);
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([getTable("PL"), getMatches()])
      .then(([t, m]) => {
        if (!alive) return;
        setTable(t);
        setMatches(m);
      })
      .catch(() => alive && (setTable(null), setMatches(null)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="match-center"
      className="mx-auto max-w-7xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10"
      data-testid="match-center"
    >
      {copy.hide_header ? null : (
        <div className="mb-6 border-b border-black/10 pb-6">
          <div className="flex items-center gap-2 text-brand-accent" style={kickerStyle(copy)}>
            <Trophy size={16} strokeWidth={2.5} />
            <span className="label-kicker">{copy.kicker}</span>
          </div>
          <h2
            className="brand-headline mt-2 whitespace-pre-line font-display text-3xl font-black tracking-tight text-[var(--brand-ink)] sm:text-4xl"
            style={titleStyle(copy)}
          >
            {copy.title}
          </h2>
          {copy.intro ? (
            <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
              {copy.intro}
            </p>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Table */}
        <div
          className="rounded-md border border-black/10 bg-white lg:col-span-2"
          data-testid="league-table"
        >
          <div className="flex items-center justify-between border-b border-black/10 px-6 py-4">
            <div>
              <p className="label-kicker text-[var(--brand-body)]">{table?.competition?.name || "League Table"}</p>
              <h3 className="font-display text-lg font-bold text-[var(--brand-ink)]">
                Standings
                {table?.competition?.current_matchday ? (
                  <span className="ml-2 label-kicker font-normal text-[var(--brand-body)]">
                    MD {table.competition.current_matchday}
                  </span>
                ) : null}
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-black/5 bg-[#F8FAFC] text-[var(--brand-body)]">
                  <th className="px-3 py-3 label-kicker">#</th>
                  <th className="px-3 py-3 label-kicker">Team</th>
                  <th className="px-2 py-3 text-right label-kicker">P</th>
                  <th className="hidden px-2 py-3 text-right label-kicker sm:table-cell">
                    W
                  </th>
                  <th className="hidden px-2 py-3 text-right label-kicker sm:table-cell">
                    D
                  </th>
                  <th className="hidden px-2 py-3 text-right label-kicker sm:table-cell">
                    L
                  </th>
                  <th className="px-2 py-3 text-right label-kicker">GD</th>
                  <th className="px-3 py-3 text-right label-kicker">Pts</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  ["s1","s2","s3","s4","s5","s6","s7","s8"].map((id) => (
                    <tr key={id} className="animate-pulse border-b border-black/5">
                      <td className="px-3 py-3" colSpan={8}>
                        <div className="h-4 w-full bg-[#F1F5F9]" />
                      </td>
                    </tr>
                  ))
                ) : !table?.table?.length ? (
                  <tr>
                    <td
                      className="px-3 py-8 text-center text-[var(--brand-body)]"
                      colSpan={8}
                    >
                      Table unavailable right now.
                    </td>
                  </tr>
                ) : (
                  table.table.map((row) => (
                    <tr
                      key={row.position}
                      data-testid={`table-row-${row.position}`}
                      className={`border-b border-black/5 transition-colors ${
                        row.is_highlight
                          ? "bg-[#6CABDD]/10 font-semibold text-[var(--brand-ink)]"
                          : "hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <td className="px-3 py-3 font-display font-bold text-[var(--brand-ink)]">
                        <span className="flex items-center gap-2">
                          {row.is_highlight ? (
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#6CABDD]" />
                          ) : null}
                          {row.position}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="font-display font-semibold">
                          {row.team.short_name}
                        </span>
                        {row.is_highlight ? (
                          <span className="ml-2 rounded-sm bg-[#6CABDD] px-1.5 py-0.5 label-kicker text-white">
                            Sky Blues
                          </span>
                        ) : null}
                      </td>
                      <td className="px-2 py-3 text-right tabular-nums">
                        {row.played}
                      </td>
                      <td className="hidden px-2 py-3 text-right tabular-nums sm:table-cell">
                        {row.won}
                      </td>
                      <td className="hidden px-2 py-3 text-right tabular-nums sm:table-cell">
                        {row.draw}
                      </td>
                      <td className="hidden px-2 py-3 text-right tabular-nums sm:table-cell">
                        {row.lost}
                      </td>
                      <td className="px-2 py-3 text-right tabular-nums">
                        {row.goal_difference > 0 ? "+" : ""}
                        {row.goal_difference}
                      </td>
                      <td className="px-3 py-3 text-right font-display text-base font-black text-[var(--brand-ink)] tabular-nums">
                        {row.points}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Fixtures + Results stacked */}
        <div className="flex flex-col gap-6">
          <div
            className="rounded-md border border-black/10 bg-white"
            data-testid="next-fixtures"
          >
            <div className="flex items-center gap-2 border-b border-black/10 px-6 py-4">
              <Calendar size={14} strokeWidth={2.5} className="text-[#6CABDD]" />
              <p className="label-kicker text-[var(--brand-body)]">All upcoming fixtures</p>
            </div>
            <ul className="max-h-[480px] overflow-y-auto">
              {loading ? (
                ["fix1","fix2","fix3"].map((id) => (
                  <li
                    key={id}
                    className="animate-pulse border-b border-black/5 px-6 py-4"
                  >
                    <div className="h-3 w-1/3 bg-[#F1F5F9]" />
                    <div className="mt-2 h-4 w-2/3 bg-[#F1F5F9]" />
                  </li>
                ))
              ) : !matches?.fixtures?.length ? (
                <li className="px-6 py-6 text-sm text-[var(--brand-body)]">
                  No upcoming fixtures.
                </li>
              ) : (
                matches.fixtures.map((m) => (
                  <li
                    key={m.id}
                    data-testid="fixture-row"
                    className="flex items-center justify-between gap-3 border-b border-black/5 px-6 py-3 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="label-kicker text-[var(--brand-body)]">
                        {m.date_human}
                      </p>
                      <p className="truncate font-display text-sm font-bold text-[var(--brand-ink)]">
                        {m.coventry_is_home ? (
                          <>
                            <span className="text-[#6CABDD]">COV</span> vs{" "}
                            {m.away.short_name}
                          </>
                        ) : (
                          <>
                            {m.home.short_name} vs{" "}
                            <span className="text-[#6CABDD]">COV</span>
                          </>
                        )}
                      </p>
                    </div>
                    <span className="rounded-sm border border-black/10 px-2 py-1 label-kicker text-[var(--brand-body)]">
                      {m.coventry_is_home ? "H" : "A"}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div
            className="rounded-md border border-black/10 bg-white"
            data-testid="last-results"
          >
            <div className="flex items-center gap-2 border-b border-black/10 px-6 py-4">
              <Flag size={14} strokeWidth={2.5} className="text-[#6CABDD]" />
              <p className="label-kicker text-[var(--brand-body)]">All results this season</p>
            </div>
            <ul className="max-h-[480px] overflow-y-auto">
              {loading ? (
                ["res1","res2","res3"].map((id) => (
                  <li
                    key={id}
                    className="animate-pulse border-b border-black/5 px-6 py-4"
                  >
                    <div className="h-3 w-1/3 bg-[#F1F5F9]" />
                    <div className="mt-2 h-4 w-2/3 bg-[#F1F5F9]" />
                  </li>
                ))
              ) : !matches?.results?.length ? (
                <li className="px-6 py-6 text-sm text-[var(--brand-body)]">
                  No results yet.
                </li>
              ) : (
                matches.results.map((m) => {
                  const tone = resultTone(m);
                  return (
                    <li
                      key={m.id}
                      data-testid="result-row"
                      className="flex items-center justify-between gap-3 border-b border-black/5 px-6 py-3 last:border-b-0"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <LabeledBadge text={tone} tone={tone} />
                        <div className="min-w-0">
                          <p className="label-kicker text-[var(--brand-body)]">
                            {m.date_human}
                          </p>
                          <p className="truncate font-display text-sm font-bold text-[var(--brand-ink)]">
                            {m.coventry_is_home ? (
                              <>
                                <span className="text-[#6CABDD]">COV</span> vs{" "}
                                {m.away.short_name}
                              </>
                            ) : (
                              <>
                                {m.home.short_name} vs{" "}
                                <span className="text-[#6CABDD]">COV</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <span className="font-display text-base font-black tabular-nums text-[var(--brand-ink)]">
                        {m.home_score}–{m.away_score}
                      </span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>
      </div>

      <p className="mt-6 text-right label-kicker text-[var(--brand-body)]">
        Data provided by{" "}
        <a
          href="https://www.football-data.org"
          target="_blank"
          rel="noreferrer"
          className="underline decoration-[#6CABDD] underline-offset-4"
        >
          football-data.org
        </a>
      </p>
    </section>
  );
};
